import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import settingsSlice, { integrationSelector } from '@/redux/features/settings.slice';
import commonSlice from '@/redux/features/common.slice';
import { useCreateIntegrationAvadaMutation, useDeleteIntegrationAvadaMutation } from '@/redux/api/api.caller';
import { handleToastMutation } from '@/helpers/toast';
import { getIntegrationErrorMessage } from '../helpers';
import { IIntegrationAvada } from '@/types/settings';
import { IntegrationAdapter } from '../types';

const validateAvadaField = (value: string): string => {
  const regEx = new RegExp('^[a-zA-Z0-9]*$');
  if (!regEx.test(value)) return 'Field is invalid';
  if (!value) return 'Field is required';
  return '';
};

const useAvadaIntegrationAdapter = (status?: IIntegrationAvada, isFetching = false): IntegrationAdapter => {
  const dispatch = useDispatch();
  const [apiError, setApiError] = useState<{ create: string; delete: string }>({ create: '', delete: '' });
  const integrationState = useSelector(integrationSelector);
  const [createAvada, createdAvadaData] = useCreateIntegrationAvadaMutation();
  const [deleteAvada, deletedAvadaData] = useDeleteIntegrationAvadaMutation();
  const [baseline, setBaseline] = useState<{ appID: string; secretKey: string }>({ appID: '', secretKey: '' });

  const isDirty =
    !integrationState.isConnected &&
    (integrationState.appID.value !== baseline.appID || integrationState.secretKey.value !== baseline.secretKey);

  const canSave =
    !integrationState.appID.error &&
    !integrationState.secretKey.error &&
    !!integrationState.appID.value &&
    !!integrationState.secretKey.value;

  const onDiscard = () => {
    dispatch(
      settingsSlice.actions.handleChangeIntegration({
        ...integrationState,
        appID: { ...integrationState.appID, value: baseline.appID, error: '' },
        secretKey: { ...integrationState.secretKey, value: baseline.secretKey, error: '' },
      }),
    );
    setApiError((prev) => ({ ...prev, create: '' }));
  };

  const onToggleShowSecret = (key: string) => {
    if (key !== 'secretKey') return;
    dispatch(
      settingsSlice.actions.handleChangeIntegration({
        ...integrationState,
        secretKey: { ...integrationState.secretKey, isShow: !integrationState.secretKey.isShow },
      }),
    );
  };

  const onFieldChange = (key: string, value: string) => {
    const error = validateAvadaField(value);
    dispatch(
      settingsSlice.actions.handleChangeIntegration({
        ...integrationState,
        [key]: { ...(integrationState as unknown as Record<string, { value: string; error: string }>)[key], value, error },
      }),
    );
    setApiError((prev) => ({ ...prev, create: '' }));
  };

  const onSave = () => {
    createAvada({ avadaAppId: integrationState.appID.value, avadaSecretKey: integrationState.secretKey.value }).then((res) => {
      if ('data' in res && res.data && res.data?.state === 1) {
        setApiError((prev) => ({ ...prev, create: '' }));
        dispatch(commonSlice.actions.handleToast(handleToastMutation(res)));
        return;
      }
      setApiError((prev) => ({ ...prev, create: getIntegrationErrorMessage(res) }));
    });
  };

  const onDisconnect = () => {
    deleteAvada().then((res) => {
      if ('data' in res && res.data && res.data?.state === 1) {
        setApiError((prev) => ({ ...prev, delete: '' }));
        dispatch(commonSlice.actions.handleToast(handleToastMutation(res)));
        dispatch(
          settingsSlice.actions.handleChangeIntegration({
            ...integrationState,
            isConnected: false,
            isCollapse: false,
            appID: { ...integrationState.appID, value: '' },
            secretKey: { ...integrationState.secretKey, value: '' },
          }),
        );
        return;
      }
      setApiError((prev) => ({ ...prev, delete: getIntegrationErrorMessage(res) }));
    });
  };

  useEffect(() => {
    if (status) {
      dispatch(
        settingsSlice.actions.handleChangeIntegration({
          ...integrationState,
          isConnected: !!status.shop,
          appID: { ...integrationState.appID, value: status.avadaAppId },
          secretKey: { ...integrationState.secretKey, value: status.avadaSecretKey },
        }),
      );
      setBaseline({ appID: status.avadaAppId || '', secretKey: status.avadaSecretKey || '' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  return {
    status: integrationState.isConnected ? 'active' : 'disconnected',
    isFetching,
    isSaving: createdAvadaData.isLoading,
    isDisconnecting: deletedAvadaData.isLoading,
    fields: [
      { key: 'appID', value: integrationState.appID.value, error: integrationState.appID.error },
      {
        key: 'secretKey',
        value: integrationState.secretKey.value,
        error: integrationState.secretKey.error,
        isShow: integrationState.secretKey.isShow,
      },
    ],
    isDirty,
    canSave,
    apiError,
    connectedSummary: integrationState.isConnected
      ? { label: 'App ID:', value: integrationState.appID.value }
      : undefined,
    onFieldChange,
    onToggleShowSecret,
    onSave,
    onDiscard,
    onDisconnect,
  };
};

export default useAvadaIntegrationAdapter;
